import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { useServices } from "../app/services";
import { useResource } from "../app/useResource";
import { MomentChip, PerformanceCard, PostCard } from "../components/cards";
import { Icon } from "../components/Icon";
import { EmptyState, PageHeader, ResourceView } from "../components/ui";
import { performerNames } from "../lib/labels";
import { Highlight } from "../motion/Highlight";
import { WelcomeGuide } from "../components/WelcomeGuide";

/**
 * 見つける：新着を基本にし、人気順だけに偏らせない（仕様書「保存・ライブラリ・発見」）。
 * メンバーの紹介・未再生の歌唱・公開されたここ好きを出す。どの枠も、並びの根拠を書いておく
 */
export function DiscoverPage() {
  const { api } = useServices();
  // 投稿の一覧（GET /posts）と未再生の歌唱（GET /performances?feed=unplayed）を組み立てる
  const res = useResource("posts.list", () => api.getDiscover(), [], ["posts"]);
  return (
    <div className="page">
      <PageHeader
        title="見つける"
        lead={
          <>
            メンバーの<Highlight>「好き」</Highlight>から、次に聴く歌を。
          </>
        }
      >
        <SearchForm />
      </PageHeader>
      <WelcomeGuide />
      <ResourceView resource={res} what="おすすめ" rows={4}>
        {(d) => (
          <>
            <section className="section" aria-labelledby="sec-posts">
              <div className="section-head">
                <h2 id="sec-posts" className="section-title">
                  メンバーのおすすめ
                </h2>
                <p className="section-basis">新しい順。人気の順には並べていません。</p>
                <Link className="btn btn-ghost btn-small" to="/posts">投稿をすべて見る</Link>
              </div>
              {d.posts.length > 0 ? (
                <ul className="post-list" data-reveal="stagger">
                  {d.posts.map((p) => (
                    <PostCard key={p.id} post={p} />
                  ))}
                </ul>
              ) : (
                <EmptyState
                  title="まだおすすめの投稿がありません"
                  icon="sparkle"
                  ribatan={{ pose: "wait", say: "最初のおすすめ、まってるよ" }}
                >
                  曲のページの「おすすめを書く」から、最初のひとことをどうぞ。
                </EmptyState>
              )}
            </section>

            <section className="section" aria-labelledby="sec-unplayed">
              <div className="section-head">
                <h2 id="sec-unplayed" className="section-title">
                  紹介されて、まだVCで流れていない歌唱
                </h2>
                <p className="section-basis">
                  メンバーの紹介があり、みんなの再生履歴でまだ一度も流れていない歌唱です（あなたの閲覧の記録は使っていません）。
                </p>
              </div>
              {d.unplayed.length > 0 ? (
                <ul className="perf-list" data-reveal="stagger">
                  {d.unplayed.map((p) => (
                    <PerformanceCard key={p.id} performance={p} />
                  ))}
                </ul>
              ) : (
                <EmptyState title="紹介された歌唱は、どれも一度は流れています" icon="check" />
              )}
            </section>

            <section className="section" aria-labelledby="sec-moments">
              <div className="section-head">
                <h2 id="sec-moments" className="section-title">
                  メンバーのここ好き
                </h2>
                <p className="section-basis">公開された「好きな瞬間」。時刻を押すと、その瞬間の説明と元動画へのリンクが出ます。</p>
              </div>
              {d.moments.length > 0 ? (
                <ul className="moment-list" data-reveal="stagger">
                  {d.moments.map((m) => (
                    <li key={m.id} className="moment-row">
                      <Link to={`/performances/${m.performance.id}`} className="moment-row__title">
                        {m.performance.work.title}
                        <span>{performerNames(m.performance)}</span>
                      </Link>
                      <MomentChip moment={m} performance={m.performance} />
                    </li>
                  ))}
                </ul>
              ) : (
                <EmptyState title="公開されたここ好きは、まだありません" icon="sparkle" />
              )}
            </section>

          </>
        )}
      </ResourceView>
    </div>
  );
}

export function SearchForm({ initial = "" }: { initial?: string }) {
  const navigate = useNavigate();
  const [q, setQ] = useState(initial);
  return (
    <form
      className="search-form"
      role="search"
      onSubmit={(e) => {
        e.preventDefault();
        if (q.trim()) navigate(`/search?q=${encodeURIComponent(q.trim())}`);
      }}
    >
      <label htmlFor="search-q" className="visually-hidden">
        曲名・歌った人・YouTubeのURLで探す
      </label>
      <Icon name="search" className="search-form__icon" />
      <input
        id="search-q"
        className="input search-form__input"
        type="search"
        enterKeyHint="search"
        placeholder="曲名・歌った人で探す"
        value={q}
        onChange={(e) => setQ(e.target.value)}
      />
      <button type="submit" className="btn btn-primary btn-small">
        探す
      </button>
    </form>
  );
}
